'use client';

import React from 'react';
import styled, { css } from 'styled-components';

export type TimeBadgeTone = 'default' | 'info' | 'success' | 'warn' | 'error';

export interface TimeBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Time-of-day or short numeric label rendered in mono. */
  time: string;
  /** Optional uppercase eyebrow above the time. */
  eyebrow?: string;
  /** Tone — drives background and ink colours. Defaults to `default`. */
  tone?: TimeBadgeTone;
  /** Render a smaller (48px) variant. Default false (62px). */
  compact?: boolean;
}

const Root = styled.div<{ $tone: TimeBadgeTone; $compact: boolean }>`
  display: grid;
  grid-auto-flow: row;
  place-items: center;
  place-content: center;
  width: ${(p) => (p.$compact ? '48px' : '62px')};
  height: ${(p) => (p.$compact ? '48px' : '62px')};
  border-radius: ${(p) => p.theme.radius.md};
  font-family: ${(p) => p.theme.fontFamily.ui};
  flex-shrink: 0;
  ${(p) => toneStyle(p.$tone, p.theme)}
`;

const Eyebrow = styled.div`
  font-size: ${(p) => p.theme.fontSize.xs};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.9;
`;

const Time = styled.div`
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.md};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  letter-spacing: 0.5px;
`;

function toneStyle(tone: TimeBadgeTone, theme: any) {
  const ink = '#ffffff';
  if (tone === 'error')
    return css`
      background: ${theme.colors.semantic.error.color};
      color: ${ink};
    `;
  if (tone === 'warn')
    return css`
      background: ${theme.colors.semantic.warn.color};
      color: ${ink};
    `;
  if (tone === 'success')
    return css`
      background: ${theme.colors.semantic.success.color};
      color: ${ink};
    `;
  if (tone === 'info')
    return css`
      background: ${theme.colors.semantic.info.color};
      color: ${ink};
    `;
  return css`
    background: ${theme.colors.neutral.bg};
    color: ${theme.colors.neutral.ink};
    border: 1px solid ${theme.colors.neutral.line};
  `;
}

/**
 * Square stacked label/time block. Used in incident lists, audit rows,
 * and any dense list where a time-of-day stamp needs to anchor a row.
 */
export function TimeBadge({
  time,
  eyebrow,
  tone = 'default',
  compact = false,
  ...rest
}: TimeBadgeProps) {
  const label = eyebrow ? `${eyebrow} ${time}` : time;
  return (
    <Root role="group" aria-label={label} $tone={tone} $compact={compact} {...rest}>
      {eyebrow && <Eyebrow data-time-badge-eyebrow>{eyebrow}</Eyebrow>}
      <Time>{time}</Time>
    </Root>
  );
}
